import React, { useState, useRef, useEffect } from 'react';
import DoctorCardNoSchedule from './DoctorCardNoSchedule';
import { toolboxService, clinicService } from '../../../services/api';
import ClinicSearch from '../ClinicSearch';

const DoctorCompareRow = ({ doctor }) => (
  <div className="text-sm space-y-1">
    <div>
      <span className="font-medium text-gray-600">Name:</span>
      <span className="ml-2 text-gray-900">{doctor?.primary_name || 'N/A'}</span>
    </div>
    <div>
      <span className="font-medium text-gray-600">Speciality:</span>
      <span className="ml-2 text-gray-700">{doctor?.speciality || 'N/A'}</span>
    </div>
    {doctor?.service_provided && (
      <div>
        <span className="font-medium text-gray-600">Services:</span>
        <p className="mt-1 text-gray-600 text-xs line-clamp-3">{doctor.service_provided}</p>
      </div>
    )}
  </div>
);

const Step2_Review = ({ doctors: initialDoctors, clid, onBack, onComplete, loading, error, setLoading, setError }) => {
  const [doctorList, setDoctorList] = useState(initialDoctors || []);
  const [clinicId, setClinicId] = useState(clid || '');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [existingDoctors, setExistingDoctors] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [expandedReplace, setExpandedReplace] = useState({});
  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (!clinicId.trim()) {
      setExistingDoctors([]);
      return;
    }
    let cancelled = false;
    setLoadingExisting(true);
    clinicService
      .getClinicDoctors(clinicId)
      .then((res) => {
        if (!cancelled) setExistingDoctors(res?.doctors || []);
      })
      .catch(() => {
        if (!cancelled) setExistingDoctors([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingExisting(false);
      });
    return () => { cancelled = true; };
  }, [clinicId]);

  const existingByPrimary = React.useMemo(() => {
    const m = {};
    for (const d of existingDoctors) {
      if (d.is_active === false) continue;
      const k = (d.primary_name || '').trim();
      if (k && !m[k]) m[k] = d;
    }
    return m;
  }, [existingDoctors]);

  const getMatchStatus = (doctor) => {
    const key = (doctor.primary_name || '').trim();
    const existing = existingByPrimary[key];
    return existing ? { status: 'replace', existing } : { status: 'create' };
  };

  const handleUpdateDoctor = (index, updatedDoctor) => {
    setDoctorList((prev) => {
      const next = [...prev];
      next[index] = updatedDoctor;
      return next;
    });
  };

  const handleRemoveDoctor = (index) => {
    setDoctorList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImport = async () => {
    if (!clinicId.trim()) {
      setError('Please select a clinic');
      return;
    }
    if (doctorList.length === 0) {
      setError('No doctors to import');
      return;
    }

    const invalid = doctorList.filter((d) => !d.primary_name || !d.speciality);
    if (invalid.length > 0) {
      setError(`${invalid.length} doctor(s) missing primary_name or speciality`);
      return;
    }
    const emptyService = doctorList.filter((d) => !(d.service_provided || '').trim());
    if (emptyService.length > 0) {
      setError(`${emptyService.length} doctor(s) have empty service_provided`);
      return;
    }

    setImporting(true);
    setError('');
    setImportResults(null);
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const doctorsToImport = doctorList.map((doctor) => ({
        doctor: {
          primary_name: doctor.primary_name,
          speciality: doctor.speciality,
          service_provided: doctor.service_provided || '',
          gender: doctor.gender,
          type: doctor.type || 'doctor',
          meta_data: doctor.meta_data,
          external_doctor_code: doctor.external_doctor_code,
        },
        schedule: {},
      }));

      const result = await toolboxService.importDoctorsAndSchedules(clinicId, doctorsToImport, signal);

      const createdDoctors = result?.created_doctors?.length || 0;
      const updatedDoctors = result?.updated_doctors || [];
      const success = Boolean(result?.success || createdDoctors > 0 || updatedDoctors.length > 0);

      if (success) {
        onComplete?.({
          success: true,
          createdDoctors,
          updatedDoctors,
          createdSchedules: 0,
          clid: clinicId,
          errors: result?.errors || [],
        });
      } else {
        setImportResults({
          success: false,
          created_doctors: result?.created_doctors || [],
          errors: result?.errors || [{ doctor: 'Unknown', error: 'Import failed' }],
        });
        setError('Import completed with errors');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Import failed';
      setError(msg);
      setImportResults({
        success: false,
        created_doctors: [],
        errors: [{ doctor: 'Unknown', error: msg }],
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 2: Preview & Import</h2>
        <p className="text-gray-600">Review doctors, select clinic, then import. Schedules will not be created.</p>
      </div>

      <ClinicSearch
        selectedClinicId={clinicId}
        onClinicSelect={setClinicId}
        error={error}
        setError={setError}
      />

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Doctors ({doctorList.length})
          {clinicId && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              {loadingExisting ? 'Loading existing...' : `${Object.keys(existingByPrimary).length} existing in clinic`}
            </span>
          )}
        </h3>
        <div className="grid grid-cols-1 gap-6">
          {doctorList.map((doctor, index) => {
            const { status, existing } = getMatchStatus(doctor);
            const isExpanded = expandedReplace[index];
            return (
              <div key={index} className="relative border rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 p-2 bg-gray-50 border-b">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded ${
                      status === 'create' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {status === 'create' ? 'Create' : 'Replace'}
                  </span>
                  {status === 'replace' && (
                    <button
                      type="button"
                      onClick={() => setExpandedReplace((p) => ({ ...p, [index]: !p[index] }))}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      {isExpanded ? 'Hide' : 'Show'} original vs updated
                    </button>
                  )}
                </div>
                <div className="p-4">
                  <DoctorCardNoSchedule
                    doctor={doctor}
                    index={index}
                    onUpdate={handleUpdateDoctor}
                  />
                  {status === 'replace' && isExpanded && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded border">
                      <div className="p-3 bg-white rounded border">
                        <h4 className="font-medium text-gray-700 mb-2">Original</h4>
                        <DoctorCompareRow doctor={existing} />
                      </div>
                      <div className="p-3 bg-blue-50 rounded border border-blue-200">
                        <h4 className="font-medium text-blue-800 mb-2">Updated (from import)</h4>
                        <DoctorCompareRow doctor={doctor} />
                      </div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveDoctor(index)}
                  className="absolute top-2 right-2 text-red-600 hover:text-red-700"
                  title="Remove"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      {importResults && !importResults.success && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-800 mb-2">Import issues</h4>
          <ul className="list-disc list-inside text-sm text-yellow-700">
            {importResults.errors?.map((e, i) => (
              <li key={i}>{`${e.doctor || 'Unknown'}: ${e.error}`}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={importing}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={handleImport}
          disabled={doctorList.length === 0 || !clinicId.trim() || importing}
          className="px-8 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {importing ? 'Importing...' : 'Import Doctors'}
        </button>
      </div>
    </div>
  );
};

export default Step2_Review;
